import { ResourceIdentifiers } from '../utils/access-control/enums/resource-identifiers';
import { isFreePlan } from '../utils/is-free-or-starter-plan';

/**
 * Plan-based feature gates for the Growth upsell modal.
 *
 * **To add a new gate:**
 *  1. Add the string to `ResourceIdentifiers` (resource-identifiers.ts).
 *  2. Add the key to `WARMUP_FEATURE_GATE_KEYS` below.
 *  3. In the component: `const isXLocked = !useHasMetaResources(ResourceIdentifiers.YOUR_KEY)` (absent ⇒ locked)
 *  4. Call `openLockedFeatureUpsell('YOUR_KEY')` from the unlock handler.
 *
 * Modal body copy: **Free** users see {@link FREE_PLAN_UPSELL_DEFINITION}; **Starter** (and other non-Free upsell tiers) see {@link STARTER_PLAN_UPSELL_DEFINITION}.
 */
const WARMUP_FEATURE_GATE_KEYS = {
  WARMUP_REPORT_ESP_ANALYTICS: ResourceIdentifiers.WARMUP_REPORT_ESP_ANALYTICS,
  WARMUP_REPORT_DELIVERABILITY: ResourceIdentifiers.WARMUP_REPORT_DELIVERABILITY,
  WARMUP_REPORT_EXPORT: ResourceIdentifiers.WARMUP_REPORT_EXPORT,
  WARMUP_SETTING_BASIC: ResourceIdentifiers.WARMUP_SETTING_BASIC,
  WARMUP_SETTING_REPLY_RATE: ResourceIdentifiers.WARMUP_SETTING_REPLY_RATE,
  WARMUP_SETTING_SENDING_SCHEDULE:
    ResourceIdentifiers.WARMUP_SETTING_SENDING_SCHEDULE,
  WARMUP_SETTING_STRATEGY: ResourceIdentifiers.WARMUP_SETTING_STRATEGY,
  WARMUP_SETTING_ESP_PRIORITIZATION:
    ResourceIdentifiers.WARMUP_SETTING_ESP_PRIORITIZATION,
  WARMUP_SETTING_WARMUP_LANGUAGE:
    ResourceIdentifiers.WARMUP_SETTING_WARMUP_LANGUAGE,
  WARMUP_SETTING_CONTENT_SETTING:
    ResourceIdentifiers.WARMUP_SETTING_CONTENT_SETTING,
} as const;

export type LockedFeatureUpsellRegistryKey = keyof typeof WARMUP_FEATURE_GATE_KEYS;

export type LockedFeatureHeroPreset = 'sentEmailLarge';

export interface LockedFeatureUpsellDefinition {
  title: string;
  subtitle: string;
  bulletLines: readonly string[];
  /** Primary CTA label when user can open billing (has subscription read). */
  upgradeButtonLabel: string;
  /** Hero illustration — extend union + `HERO_IMAGES` in modal when new art is needed. */
  heroPreset?: LockedFeatureHeroPreset;
}

const FREE_PLAN_TITLE = 'Upgrade to Access Advanced Features';
const FREE_PLAN_SUBTITLE =
  'Get higher sending limits, better deliverability control, and deeper inbox insights.';

const FREE_PLAN_BULLET_LINES = [
  'Higher daily warm-up sending limits across all connected accounts',
  'ESP-level analytics across providers like Google and Microsoft',
  'Better inbox placement visibility and delivery insights',
  'AI-powered content targeting by industry and topic',
  'Custom sending schedules, language, and sending controls',
  'Advanced reply optimization and smarter ESP distribution',
] as const;

/** Upsell copy shown when the workspace is on the Free plan. */
export const FREE_PLAN_UPSELL_DEFINITION: LockedFeatureUpsellDefinition = {
  title: FREE_PLAN_TITLE,
  subtitle: FREE_PLAN_SUBTITLE,
  bulletLines: FREE_PLAN_BULLET_LINES,
  upgradeButtonLabel: 'Upgrade Plan',
  heroPreset: 'sentEmailLarge',
};

const STARTER_GROWTH_UPGRADE_LABEL =
  'Upgrade to Growth Plan for Advance Features';
const STARTER_GROWTH_UPGRADE_SUBTITLE =
  'More control, better inbox placement, 1,000 emails per day.';

const STARTER_GROWTH_BULLET_LINES = [
  '1,000 warmup emails per day across all accounts (5x more)',
  'ESP-based analytics, delivery breakdown by Google, Microsoft and Others',
  'AI content targeting by industry and topic',
  'Custom schedule, language and sending controls',
  'Advanced reply rate up to 45%',
  'Access to all 4 warmup strategies and custom ESP distribution',
] as const;

/** Upsell copy for Starter (non-Free) workspaces gated from Growth warmup features. */
export const STARTER_PLAN_UPSELL_DEFINITION: LockedFeatureUpsellDefinition = {
  title: STARTER_GROWTH_UPGRADE_LABEL,
  subtitle: STARTER_GROWTH_UPGRADE_SUBTITLE,
  bulletLines: STARTER_GROWTH_BULLET_LINES,
  upgradeButtonLabel: STARTER_GROWTH_UPGRADE_LABEL,
  heroPreset: 'sentEmailLarge',
};

/** Resolves headline, bullets, and default primary CTA label for the user's plan tier. */
export function getLockedFeatureUpsellCopyForPlan(
  planCode: string | undefined,
): LockedFeatureUpsellDefinition {
  if (isFreePlan(planCode)) {
    return FREE_PLAN_UPSELL_DEFINITION;
  }

  return STARTER_PLAN_UPSELL_DEFINITION;
}

function buildDefaultLockedFeatureUpsellRecord(): Record<
  LockedFeatureUpsellRegistryKey,
  LockedFeatureUpsellDefinition
> {
  const keys = Object.keys(WARMUP_FEATURE_GATE_KEYS) as LockedFeatureUpsellRegistryKey[];

  return keys.reduce(
    (acc, key) => ({
      ...acc,
      [key]: STARTER_PLAN_UPSELL_DEFINITION,
    }),
    {} as Record<LockedFeatureUpsellRegistryKey, LockedFeatureUpsellDefinition>,
  );
}

/** Per-key map (Starter/Growth copy); runtime modal uses {@link getLockedFeatureUpsellCopyForPlan}. */
export const LOCKED_FEATURE_UPSELL = buildDefaultLockedFeatureUpsellRecord();
